// Camera and View management for isometric perspective
import * as THREE from 'three';
import gsap from 'gsap';

export class View {
    private static instance: View;

    public camera!: THREE.OrthographicCamera;
    public position: THREE.Vector3 = new THREE.Vector3();
    public target: THREE.Vector3 = new THREE.Vector3();

    // Isometric angle settings
    private readonly ISO_ANGLE = Math.PI / 6; // 30 degrees
    private readonly CAMERA_HEIGHT = 30;
    private readonly CAMERA_DISTANCE = 60;

    private zoom: number = 1;
    private targetZoom: number = 1;
    private aspect: number = 1;
    private frustumSize: number = 20;

    // Camera animation state
    private isAnimating: boolean = false;
    private animationTarget: THREE.Vector3 = new THREE.Vector3();

    private constructor(container: HTMLElement) {
        this.aspect = container.clientWidth / container.clientHeight;
        this.setupCamera();
        this.setupListeners(container);
        // Initial resize to set correct frustum
        this.resize(container);
    }

    static init(container: HTMLElement): View {
        if (!View.instance) {
            View.instance = new View(container);
        }
        return View.instance;
    }

    static getInstance(): View {
        return View.instance;
    }

    private setupCamera(): void {
        const halfWidth = this.frustumSize * this.aspect / 2;
        const halfHeight = this.frustumSize / 2;

        this.camera = new THREE.OrthographicCamera(
            -halfWidth,  // left
            halfWidth,   // right
            halfHeight,  // top
            -halfHeight, // bottom
            0.1,         // near
            1000         // far
        );

        // Set isometric position
        this.updateCameraPosition();
    }

    // View modes
    private viewMode: 'isometric' | 'front' | 'intro' = 'intro'; // Start in intro
    private frontOffset = { y: 12, z: 50 }; // Lower, closer, frontal

    private updateCameraPosition(): void {
        let targetPos = this.isAnimating ? this.animationTarget : this.target;

        // Override target for intro to maximize focus on the heart
        if (this.viewMode === 'intro') {
            targetPos = new THREE.Vector3(0, 5, 0);
        }

        let x = 0, y = 0, z = 0;

        if (this.viewMode === 'isometric') {
            // Classic isometric view angle
            x = this.CAMERA_DISTANCE * Math.cos(this.ISO_ANGLE);
            y = this.CAMERA_HEIGHT;
            z = this.CAMERA_DISTANCE * Math.sin(this.ISO_ANGLE);
        } else if (this.viewMode === 'front') {
            // Front view (looking directly at subject)
            // Assuming subject faces +Z or we look from +Z towards -Z
            x = 0;
            // On mobile (portrait), we want to lower the camera so character appears higher
            const isPortrait = this.aspect < 1.0;
            const mobileYOffset = isPortrait ? -3.0 : 0;

            y = this.frontOffset.y + mobileYOffset;
            z = this.frontOffset.z;
        } else if (this.viewMode === 'intro') {
            // Intro: Look at the heart (target set to 0,5,0 externally)
            // Camera position: Close up, straight on
            x = 0;
            y = 0; // Relative to target
            z = 20;
        }

        this.camera.position.set(
            targetPos.x + x,
            targetPos.y + y,
            targetPos.z + z
        );

        this.camera.lookAt(targetPos);
        this.camera.zoom = this.zoom;
        this.camera.updateProjectionMatrix();
    }

    private setupListeners(container: HTMLElement): void {
        // Handle resize
        window.addEventListener('resize', () => {
            this.resize(container);
        });

        // Handle scroll for zoom
        container.addEventListener('wheel', (e) => {
            // Prevent zoom if scrolling on a UI element that should interpret the scroll itself
            // or if the user is interacting with an overlay
            const target = e.target as HTMLElement;
            const isUIInteraction = target.closest('.ui-overlay') ||
                target.closest('.summary-screen') ||
                target.closest('.scrollable') ||
                target.tagName.toLowerCase() === 'button' ||
                target.tagName.toLowerCase() === 'input' ||
                target.tagName.toLowerCase() === 'textarea';

            if (isUIInteraction) {
                // If the element is scrollable, we let it scroll.
                // If it's just a UI element (like a button) we still probably don't want to zoom 
                // while the mouse is over it to avoid accidental zooms.
                return;
            }

            e.preventDefault();
            this.targetZoom = Math.max(0.2, Math.min(3, this.targetZoom - e.deltaY * 0.001));
        }, { passive: false });
    }

    resize(container: HTMLElement): void {
        this.aspect = container.clientWidth / container.clientHeight;

        // Dynamic frustum size for mobile support
        // We need to ensure a minimum accessible width to see the character at x=-12
        const MIN_HORIZONTAL_WIDTH = 32; // -16 to +16 coverage
        const BASE_FRUSTUM_SIZE = 20;

        // Calculate required frustum size to maintain min width
        // currentWidth = size * aspect
        // requiredSize = minWidth / aspect

        if (BASE_FRUSTUM_SIZE * this.aspect < MIN_HORIZONTAL_WIDTH) {
            this.frustumSize = MIN_HORIZONTAL_WIDTH / this.aspect;
        } else {
            this.frustumSize = BASE_FRUSTUM_SIZE;
        }

        const halfWidth = this.frustumSize * this.aspect / 2;
        const halfHeight = this.frustumSize / 2;

        this.camera.left = -halfWidth;
        this.camera.right = halfWidth;
        this.camera.top = halfHeight;
        this.camera.bottom = -halfHeight;
        this.camera.updateProjectionMatrix();
    }

    setTarget(x: number, y: number, z: number): void {
        this.target.set(x, y, z);
        this.updateCameraPosition();
    }

    setZoom(zoom: number): void {
        this.targetZoom = Math.max(0.5, Math.min(3, zoom));
    }

    update(): void {
        // Smooth zoom interpolation
        this.zoom += (this.targetZoom - this.zoom) * 0.1;

        this.updateCameraPosition();
    }

    // Smooth pan camera to focus on a world position
    focusOnPosition(x: number, z: number, y: number = 0, duration: number = 1.0): Promise<void> {
        return new Promise((resolve) => {
            this.isAnimating = true;
            this.animationTarget.copy(this.target);

            gsap.to(this.animationTarget, {
                x: x,
                z: z,
                y: y,
                duration: duration,
                ease: 'power2.inOut',
                onUpdate: () => {
                    this.target.copy(this.animationTarget);
                },
                onComplete: () => {
                    this.isAnimating = false;
                    resolve();
                }
            });
        });
    }

    // Zoom in/out smoothly
    animateZoom(targetZoom: number, duration: number = 0.5): Promise<void> {
        return new Promise((resolve) => {
            gsap.to(this, {
                targetZoom: Math.max(0.5, Math.min(3, targetZoom)),
                duration: duration,
                ease: 'power2.inOut',
                onComplete: () => resolve()
            });
        });
    }

    // Focus on a position and switch to front view (for dialogue)
    async transitionToFrontView(x: number, z: number, offsetX: number = 0): Promise<void> {
        this.viewMode = 'front';

        await Promise.all([
            // Target Y=3.0 to shift everything down (so character at Y=0 appears lower)
            this.focusOnPosition(x + offsetX, z, 3.0, 1.2),
            this.animateZoom(2.5, 1.2)
        ]);
    }

    // Return to default view
    async resetView(): Promise<void> {
        this.viewMode = 'isometric';

        await Promise.all([
            // Return to Y=0
            this.focusOnPosition(0, 0, 0, 0.8),
            this.animateZoom(1, 0.8)
        ]);
    }

    // Transition from heart Intro to Character
    async transitionFromIntro(targetX: number, targetZ: number): Promise<void> {
        // Move from Intro View (0,5,0) to Front View (Character)
        this.viewMode = 'front';

        // We want to smoothly fly to the character
        // Start by ensuring we are zoomed in/out appropriately

        await Promise.all([
            this.focusOnPosition(targetX, targetZ, 3.0, 2.0), // Slower fly
            this.animateZoom(2.5, 2.0)
        ]);
    }

    // Get world position from screen coordinates
    screenToWorld(screenX: number, screenY: number, container: HTMLElement): THREE.Vector3 {
        const rect = container.getBoundingClientRect();
        const x = ((screenX - rect.left) / rect.width) * 2 - 1;
        const y = -((screenY - rect.top) / rect.height) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);

        // Intersect with ground plane (y = 0)
        const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        const intersection = new THREE.Vector3();
        raycaster.ray.intersectPlane(plane, intersection);

        return intersection;
    }
}
